/**
 * AutoScheduleControl.tsx
 * The single auto-schedule control in the Scenario Phase-1 header.
 *
 * BUG-2026-0132 #3 — the inline "Default machines / Shortest time" toggle + a
 * separate "Set" button were redundant: the user has already changed the date,
 * so clicking Set again is busy-work. Now changing the Project Start Date opens
 * a confirmation dialog that explains the consequence (every TENTATIVE booking,
 * including manual drags, is re-placed from that date — committed bookings keep
 * their dates, G2-5) and asks WHICH strategy to use. The strategy buttons in the
 * dialog ARE the confirm action; Cancel restores the date. A one-click Undo
 * appears after a re-plan and restores the exact pre-replan tentative placement.
 * The summary discloses committed-kept counts (G2-5) and the past-date clamp
 * (G2-8: the server floors placement at today; the picker sets min=today).
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, TextField, Tooltip, Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import UndoIcon from '@mui/icons-material/Undo';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SpeedIcon from '@mui/icons-material/Speed';
import { alpha, useTheme } from '@mui/material/styles';
import {
  replanSchedule,
  restorePlacement,
  type AutoScheduleStrategy,
  type ReplanUndo,
  type GenerateTasksResult,
} from '../../../../services/scheduleTaskGeneration';

/** Today's ISO date 'YYYY-MM-DD' (local). */
function todayISO(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 10);
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

interface AutoScheduleControlProps {
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  scenarioKey: string | null;
  /** Earliest start across the scenario's bookings — the default date. */
  currentStart: string | null;
  disabled?: boolean;
  /** Reload tasks + grid after a re-plan / undo. */
  onApplied: () => Promise<void> | void;
  onFlash: (msg: string) => void;
  onError: (msg: string) => void;
}

function summarise(
  r: GenerateTasksResult,
  strategy: AutoScheduleStrategy,
  date: string,
  committedKept?: { count: number; taskNames: string[] },
): string {
  const how = strategy === 'minimize_span' ? 'across multiple machines (shortest time)' : 'on default machines';
  // G2-8: a past date is floored at today server-side — say so.
  const clamped = r.projectStartClamped
    ? ` ⚠ ${r.projectStartClamped.requested} is in the past — bookings were placed from ${r.projectStartClamped.effective} (today) instead.`
    : '';
  // G2-5 (audit #2): replan is tentative-only — disclose the committed
  // bookings that kept their dates instead of flashing an unexplained "0 placed".
  const kept = (committedKept?.count ?? 0) > 0
    ? ` ${committedKept!.count} committed booking(s) kept their dates${
        committedKept!.taskNames.length > 0
          ? ` (${committedKept!.taskNames.slice(0, 3).join(', ')}${committedKept!.taskNames.length > 3 ? ', …' : ''})`
          : ''
      } — reopen or edit them individually to move them.`
    : '';
  const unplaced = r.unplacedTaskTypes.length > 0
    ? ` ${r.unplacedTaskTypes.length} task type(s) had no capable machine.`
    : '';
  const unmapped = (r.unmappedTests?.length ?? 0) > 0
    ? ` ⚠ ${r.unmappedTests!.length} test(s) couldn't be added (no catalog mapping): ${r.unmappedTests!.join(', ')}.`
    : '';
  // Migration 111: catalog items with "Include in Schedule" unchecked are
  // skipped by design — an informational note, not a warning.
  const excluded = (r.excludedTests?.length ?? 0) > 0
    ? ` ${r.excludedTests!.length} test(s) excluded from scheduling by the test catalogue: ${r.excludedTests!.join(', ')}.`
    : '';
  // RM-2: a taxonomy run-mode flip is NOT adopted while other scenarios pin
  // the shared task — say so instead of letting the flip silently no-op.
  const pinned = (r.runModePinnedTasks?.length ?? 0) > 0
    ? ` ⚠ Run-mode change not adopted for ${r.runModePinnedTasks!.join(', ')} — other scenarios pin this task; release or replan them first, then sync.`
    : '';
  return `Re-planned from ${date} — ${r.bookingsCreated} tentative booking(s) placed ${how}.${clamped}${kept}${unplaced}${unmapped}${excluded}${pinned}`;
}

export const AutoScheduleControl: React.FC<AutoScheduleControlProps> = ({
  source, sourceKey, scenarioKey, currentStart, disabled = false, onApplied, onFlash, onError,
}) => {
  const theme = useTheme();
  const [value, setValue] = useState<string>(currentStart ?? todayISO());
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  // The new date awaiting a strategy choice — non-null opens the confirm dialog.
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  // Undo token from the last re-plan (cleared after undo / a new re-plan).
  const [undo, setUndo] = useState<ReplanUndo | null>(null);

  // Track the scenario's earliest start unless the user is mid-edit.
  useEffect(() => {
    if (!touched && currentStart) setValue(currentStart);
  }, [currentStart, touched]);

  // A different scenario invalidates the undo token + any pending change.
  useEffect(() => { setUndo(null); setPendingDate(null); }, [scenarioKey]);

  const appliedDate = currentStart ?? '';

  // Date edit: when it lands on a NEW valid date, open the confirm dialog. The
  // dialog's strategy buttons run the re-plan; Cancel restores the prior date.
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setValue(v);
    setTouched(true);
    if (disabled || busy || !scenarioKey) return;
    if (DATE_RE.test(v) && v !== appliedDate) setPendingDate(v);
  };

  const cancelPending = () => {
    setPendingDate(null);
    setValue(appliedDate || todayISO());
    setTouched(false);
  };

  const runReplan = async (strategy: AutoScheduleStrategy) => {
    if (!pendingDate || !scenarioKey) return;
    const date = pendingDate;
    setBusy(true);
    try {
      const res = await replanSchedule({ source, sourceKey, scenarioKey, strategy, projectStart: date });
      setUndo(res.undo);
      setPendingDate(null);
      setTouched(false);
      await onApplied();
      // P-6: staffing follows the re-plan; surface any (task, user) pairs whose
      // task could not be placed — informational only, no dialog.
      const dropped = res.staffing?.staffingDropped ?? 0;
      const droppedSuffix = dropped > 0
        ? ` ⚠ ${dropped} staffing assignment(s) could not be re-attached (task not placed) — re-add them in Phase 2.`
        : '';
      onFlash(summarise(res.result, strategy, date, res.committedKept) + droppedSuffix);
    } catch (e) {
      // Surface the backend's message (e.g. the scheduled-plan lock 409 "Reopen it…")
      // instead of a generic "Request failed with status code 409".
      const apiMsg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      onError(apiMsg ?? (e instanceof Error ? e.message : 'Failed to re-plan the schedule.'));
      cancelPending();
    } finally {
      setBusy(false);
    }
  };

  const handleUndo = async () => {
    if (!undo || !scenarioKey || busy) return;
    setBusy(true);
    try {
      const out = await restorePlacement({ source, sourceKey, scenarioKey, undo });
      setUndo(null);
      await onApplied();
      // P-6: include the restored-staffing count when the snapshot carried any.
      const staffed = out.assignmentsRestored ?? 0;
      onFlash(
        'Re-plan undone — the previous placement was restored.'
        + (staffed > 0 ? ` ${staffed} staffing assignment(s) restored.` : ''),
      );
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to undo the re-plan.');
    } finally {
      setBusy(false);
    }
  };

  // Dialog strategy button — icon + label + sub-text, runs the re-plan on click.
  const strategyButton = (
    strategy: AutoScheduleStrategy,
    icon: React.ReactNode,
    label: string,
    sub: string,
  ) => (
    <Button
      onClick={() => { void runReplan(strategy); }}
      disabled={busy}
      variant="outlined"
      startIcon={busy ? <CircularProgress size={16} /> : icon}
      sx={{
        textTransform: 'none', justifyContent: 'flex-start', textAlign: 'left',
        flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, py: 1, px: 1.5,
        borderColor: alpha(theme.palette.primary.main, 0.4),
        '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700, fontSize: '0.82rem' }}>
        {label}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.3 }}>
        {sub}
      </Typography>
    </Button>
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Tooltip title="Change this date to auto-schedule the whole scenario to start on it. You'll pick a strategy and can Undo afterwards.">
          <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        </Tooltip>
        <TextField
          type="date"
          size="small"
          label="Project Start Date"
          value={value}
          onChange={handleDateChange}
          disabled={disabled || busy}
          InputLabelProps={{ shrink: true, sx: { fontSize: '0.78rem' } }}
          // G2-8: machines can't be booked in the past — the picker floors at
          // today (typed past dates are clamped server-side, with a note).
          inputProps={{ min: todayISO(), style: { fontSize: '0.78rem', padding: '6px 8px' } }}
          sx={{ width: 168 }}
        />

        {/* Standalone auto-schedule (2026-06-25): open the strategy picker WITHOUT having to
            invent a new date — re-plans from the current start. Fixes the "couldn't run
            shortest time without changing the project date" gap. */}
        <Tooltip title="Auto-schedule from the current start date — pick Default machines or Shortest time (you can Undo afterwards).">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => {
                if (disabled || busy || !scenarioKey) return;
                const base = value && DATE_RE.test(value) ? value : (appliedDate || todayISO());
                setPendingDate(base);
              }}
              disabled={disabled || busy || !scenarioKey}
              startIcon={<SpeedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontSize: '0.78rem' }}
            >
              Auto-schedule
            </Button>
          </span>
        </Tooltip>

        {/* G2-5: honest Undo copy — the restore is tentative-only too (committed
            bookings were never moved by the re-plan, so there is nothing to undo there). */}
        {undo && (
          <Tooltip title="Restore the tentative placement from before the last re-plan (including any manual drags). Committed bookings were never moved.">
            <span>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => { void handleUndo(); }}
                disabled={busy}
                startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', fontSize: '0.78rem', color: 'text.secondary' }}
              >
                Undo
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Confirm + strategy picker (opens on a date change) */}
      <Dialog open={pendingDate !== null} onClose={() => { if (!busy) cancelPending(); }} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          Re-schedule from {pendingDate}?
        </DialogTitle>
        <DialogContent>
          {/* G2-5 (audit #2): honest copy — the re-plan is TENTATIVE-only by
              design; committed bookings never move. G2-8: past dates are
              floored at today (gentle note below). */}
          <DialogContentText sx={{ fontSize: '0.85rem', mb: 1.5 }}>
            This auto-schedules the scenario to start on{' '}
            <strong>{pendingDate}</strong>, re-placing every <strong>tentative</strong> booking
            (including any you placed by hand). Committed bookings stay where they are —
            reopen or edit them individually to move them. You can Undo right afterwards.
            Choose how to place the tests:
          </DialogContentText>
          {pendingDate !== null && pendingDate < todayISO() && (
            <DialogContentText sx={{ fontSize: '0.78rem', mb: 1.5, color: 'warning.main' }}>
              {pendingDate} is in the past — bookings will be placed from today instead.
            </DialogContentText>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {strategyButton(
              'use_defaults',
              <PrecisionManufacturingIcon sx={{ fontSize: 18 }} />,
              'Default machines',
              'Each test on its preferred/default machine, packed in sequence. Fewest machines, honours lab conventions.',
            )}
            {strategyButton(
              'minimize_span',
              <SpeedIcon sx={{ fontSize: 18 }} />,
              'Shortest time',
              'Spread tests across all capable machines (and split long tests) to finish as soon as possible.',
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancelPending} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
